import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FiFilter, 
  FiSearch, 
  FiGrid, 
  FiList,
  FiChevronDown,
  FiX,
  FiStar,
  FiUsers,
  FiClock,
  FiDollarSign,
  FiTrendingUp,
  FiBox
} from 'react-icons/fi';
import { 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaWhatsapp, 
  FaTelegram, 
  FaTiktok,
  FaLinkedin,
  FaYoutube,
  FaReddit,
  FaDiscord,
  FaSnapchat,
  FaPinterest,
  FaTumblr,
  FaSlack,
  FaTwitch,
  FaGithub,
  FaEnvelope,
  FaMobileAlt,
  FaShieldAlt
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { fetchAccounts, setFilters, clearFilters } from '../store/slices/accountSlice';
import { purchaseAccount } from '../store/slices/paymentSlice';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../components/ui/GlassCard';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';

export const AccountsPage = () => {
  const dispatch = useDispatch();
  const { accounts, filters, isLoading } = useSelector((state) => state.accounts);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('newest');
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [confirmingAccount, setConfirmingAccount] = useState(null);
  const [purchasingId, setPurchasingId] = useState(null);
  const [purchaseError, setPurchaseError] = useState('');

  useEffect(() => {
    dispatch(fetchAccounts(filters));
  }, [dispatch, filters]);

  const platformIcons = {
    Facebook: FaFacebook,
    Instagram: FaInstagram,
    Twitter: FaTwitter,
    'X (Twitter)': FaTwitter,
    WhatsApp: FaWhatsapp,
    Telegram: FaTelegram,
    TikTok: FaTiktok,
    LinkedIn: FaLinkedin,
    YouTube: FaYoutube,
    Reddit: FaReddit,
    Discord: FaDiscord,
    Snapchat: FaSnapchat,
    Pinterest: FaPinterest,
    Tumblr: FaTumblr,
    Slack: FaSlack,
    Twitch: FaTwitch,
    GitHub: FaGithub,
    'Google Voice': FaMobileAlt,
    'TextPlus': FaMobileAlt,
    'Talkatone': FaMobileAlt,
    'VPN': FaShieldAlt,
    'ExpressVPN': FaShieldAlt,
    'NordVPN': FaShieldAlt,
    'Surfshark': FaShieldAlt,
    'CyberGhost': FaShieldAlt,
    'ProtonVPN': FaShieldAlt,
    'WeChat': FaEnvelope,
    'Viber': FaMobileAlt,
    'Line': FaEnvelope,
    'Messenger': FaEnvelope,
    'Skype': FaEnvelope,
    'Email': FaEnvelope,
  };

  const platformColors = {
    Facebook: 'from-blue-600 to-blue-400',
    Instagram: 'from-purple-600 to-pink-500',
    Twitter: 'from-sky-400 to-blue-500',
    'X (Twitter)': 'from-gray-800 to-gray-600',
    WhatsApp: 'from-green-500 to-emerald-400',
    Telegram: 'from-blue-400 to-cyan-400',
    TikTok: 'from-gray-900 to-gray-700',
    LinkedIn: 'from-blue-700 to-blue-500',
    YouTube: 'from-red-600 to-red-400',
    Reddit: 'from-orange-600 to-orange-400',
    Discord: 'from-indigo-600 to-purple-500',
    Snapchat: 'from-yellow-400 to-yellow-300',
    Pinterest: 'from-red-700 to-red-500',
    Tumblr: 'from-indigo-700 to-indigo-500',
    Slack: 'from-pink-500 to-purple-500',
    Twitch: 'from-purple-700 to-purple-500',
    GitHub: 'from-gray-800 to-gray-600',
    'Google Voice': 'from-blue-600 to-orange-400',
    'TextPlus': 'from-purple-600 to-pink-400',
    'Talkatone': 'from-orange-600 to-yellow-400',
    'VPN': 'from-emerald-600 to-teal-500',
    'ExpressVPN': 'from-yellow-400 to-orange-500',
    'NordVPN': 'from-orange-600 to-red-500',
    'Surfshark': 'from-green-600 to-emerald-500',
    'CyberGhost': 'from-purple-600 to-pink-500',
    'ProtonVPN': 'from-purple-700 to-blue-600',
    'WeChat': 'from-green-700 to-green-500',
    'Viber': 'from-purple-700 to-purple-500',
    'Line': 'from-green-400 to-green-600',
    'Messenger': 'from-blue-600 to-indigo-500',
    'Skype': 'from-sky-500 to-blue-500',
    'Email': 'from-gray-600 to-gray-800',
  };

  const platforms = [
    { name: 'Facebook', icon: FaFacebook },
    { name: 'Instagram', icon: FaInstagram },
    { name: 'Twitter', icon: FaTwitter },
    { name: 'X (Twitter)', icon: FaTwitter },
    { name: 'TikTok', icon: FaTiktok },
    { name: 'YouTube', icon: FaYoutube },
    { name: 'LinkedIn', icon: FaLinkedin },
    { name: 'Snapchat', icon: FaSnapchat },
    { name: 'Reddit', icon: FaReddit },
    { name: 'Pinterest', icon: FaPinterest },
    { name: 'Tumblr', icon: FaTumblr },
    { name: 'Discord', icon: FaDiscord },
    { name: 'Twitch', icon: FaTwitch },
    { name: 'WeChat', icon: FaEnvelope },
    { name: 'Viber', icon: FaMobileAlt },
    { name: 'Line', icon: FaEnvelope },
    { name: 'WhatsApp', icon: FaWhatsapp },
    { name: 'Telegram', icon: FaTelegram },
    { name: 'Messenger', icon: FaEnvelope },
    { name: 'Skype', icon: FaEnvelope },
    { name: 'Google Voice', icon: FaMobileAlt },
    { name: 'TextPlus', icon: FaMobileAlt },
    { name: 'Talkatone', icon: FaMobileAlt },
    { name: 'GitHub', icon: FaGithub },
    { name: 'Slack', icon: FaSlack },
    { name: 'ExpressVPN', icon: FaShieldAlt },
    { name: 'NordVPN', icon: FaShieldAlt },
    { name: 'Surfshark', icon: FaShieldAlt },
    { name: 'CyberGhost', icon: FaShieldAlt },
    { name: 'ProtonVPN', icon: FaShieldAlt },
    { name: 'Email', icon: FaEnvelope },
  ];

  const handleTypeToggle = (type) => {
    const updatedTypes = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    setSelectedTypes(updatedTypes);
  };

  const applyFilters = () => {
    const filtersToApply = {
      type: selectedTypes.length > 0 ? selectedTypes.join(',') : undefined,
      minPrice: priceRange.min,
      maxPrice: priceRange.max,
      sort: sortBy
    };

    const cleanedFilters = Object.fromEntries(
      Object.entries(filtersToApply).filter(([, value]) => value !== undefined && value !== '')
    );

    dispatch(setFilters(cleanedFilters));
    setShowFilters(false);
  };

  const clearAllFilters = () => {
    setSelectedTypes([]);
    setPriceRange({ min: '', max: '' });
    setSortBy('newest');
    dispatch(clearFilters());
    setShowFilters(false);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const pageTitle = 'Browse Verified Social Media Accounts | Nitro Logs';
  const pageDescription = 'Discover premium verified social media accounts with real followers across Facebook, Instagram, Twitter, TikTok and more. Fast delivery, secure verification, and full account details.';
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://nitrologs.com';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : `${siteUrl}/accounts`;
  const imageUrl = `${siteUrl}/nitrologs.png`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: pageUrl,
    name: pageTitle,
    description: pageDescription,
    publisher: {
      '@type': 'Organization',
      name: 'Nitro Logs',
      url: siteUrl,
      logo: `${siteUrl}/nitrologs.png`
    }
  };
  const [showVideoModal, setShowVideoModal] = useState(false);

  const openVideoModal = () => setShowVideoModal(true);
  const closeVideoModal = () => setShowVideoModal(false);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="verified social media accounts, buy Instagram accounts, buy TikTok accounts, Facebook accounts, Twitter accounts, virtual numbers, account verification" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:alt" content="Nitro Logs verified social media accounts" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="w-full px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <motion.div
          variants={fadeInUp}
          initial="initial"
          animate="animate"
          className="mb-6 sm:mb-8 "
        >
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-luxury-900 mb-2 sm:mb-4">
                Browse <span className="gradient-text">Accounts</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-luxury-600">
                Discover premium social media accounts with real followers
              </p>
            </div>

            <button
              type="button"
              onClick={openVideoModal}
              className="px-4 py-2 mt-2 rounded-lg border border-white/20 bg-secondary-600 text-white hover:bg-secondary-900 cursor-pointer transition-colors"
            >
              Watch how to buy
            </button>
          </div>
        </motion.div>

        {/* Search and Filter Bar */}
        <motion.div
          variants={fadeInUp}
          initial="initial"
          animate="animate"
          className="glass rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-6 sm:mb-8"
        >
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-luxury-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search by title, description..."
                className="input-luxury pl-9 sm:pl-12 w-full text-sm sm:text-base"
                value={filters.search || ''}
                onChange={(e) => dispatch(setFilters({ search: e.target.value }))}
              />
            </div>

            {/* Filter Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl flex items-center justify-center space-x-2 transition-all duration-300 text-sm sm:text-base ${
                  showFilters 
                    ? 'bg-primary-600 text-white' 
                    : 'glass hover:bg-primary-50 text-luxury-700'
                }`}
              >
                <FiFilter className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="inline">Platforms</span>
                <FiChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 ${
                  showFilters ? 'rotate-180' : ''
                }`} />
              </button>

              {/* View Toggle */}
              <div className="glass rounded-lg sm:rounded-xl flex p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 sm:p-3 rounded-lg transition-all duration-300 ${
                    viewMode === 'grid' 
                      ? 'bg-primary-600 text-white' 
                      : 'text-luxury-400 hover:text-luxury-600'
                  }`}
                >
                  <FiGrid className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 sm:p-3 rounded-lg transition-all duration-300 ${
                    viewMode === 'list' 
                      ? 'bg-primary-600 text-white' 
                      : 'text-luxury-400 hover:text-luxury-600'
                  }`}
                >
                  <FiList className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="pt-4 sm:pt-6 mt-3 sm:mt-4 border-t border-luxury-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Platform Types */}
                    <div>
                      <h3 className="font-semibold text-luxury-900 mb-2 sm:mb-3 text-sm sm:text-base">Platform</h3>
                      <div className="space-y-1 sm:space-y-2 max-h-64 overflow-y-auto pr-2">
                        {platforms.map((platform) => {
                          const Icon = platform.icon;
                          const isSelected = selectedTypes.includes(platform.name);
                          return (
                            <button
                              key={platform.name}
                              onClick={() => handleTypeToggle(platform.name)}
                              className={`w-full flex items-center justify-between p-1.5 sm:p-2 rounded-lg transition-all duration-300 text-sm ${
                                isSelected 
                                  ? 'bg-primary-100 text-primary-700' 
                                  : 'hover:bg-luxury-100 text-luxury-600'
                              }`}
                            >
                              <div className="flex items-center space-x-2">
                                <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="text-xs sm:text-sm">{platform.name}</span>
                              </div>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-3 h-3 sm:w-4 sm:h-4 rounded"
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Price Range */}
                    <div>
                      <h3 className="font-semibold text-luxury-900 mb-2 sm:mb-3 text-sm sm:text-base">Price Range (₦)</h3>
                      <div className="space-y-2 sm:space-y-3">
                        <input
                          type="number"
                          placeholder="Min"
                          value={priceRange.min}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                          className="input-luxury text-sm sm:text-base"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={priceRange.max}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                          className="input-luxury text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    {/* Sort By */}
                    <div>
                      <h3 className="font-semibold text-luxury-900 mb-2 sm:mb-3 text-sm sm:text-base">Sort By</h3>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="input-luxury text-sm sm:text-base"
                      >
                        <option value="newest">Newest First</option>
                        <option value="price_low">Price: Low to High</option>
                        <option value="price_high">Price: High to Low</option>
                        <option value="followers_high">Most Followers</option>
                        <option value="oldest">Oldest First</option>
                      </select>
                    </div>
                  </div>

                  {/* Filter Actions */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-luxury-200">
                    <button
                      onClick={() => {
                        clearAllFilters();
                      }}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 text-sm sm:text-base text-luxury-600 hover:text-luxury-900 transition-colors"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={applyFilters}
                      className="w-full sm:w-auto luxury-button text-sm sm:text-base"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Wallet / Balance */}
        {user && (
          <GlassCard className="p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Wallet Balance</p>
              <p className="text-2xl font-semibold">₦{user.balance?.toLocaleString() || 0}</p>
            </div>
            <div className="flex items-center gap-3">
              {/* <span className="text-sm text-gray-400">Available for purchases</span> */}
              <button
                type="button"
                onClick={() => navigate('/deposit')}
                className="px-4 py-2 rounded-lg bg-linear-to-r from-yellow-500 to-orange-500 text-white hover:opacity-95 transition-colors"
              >
                Add Funds
              </button>
            </div>
          </GlassCard>
        )}

        {/* Active Filters */}
        {(selectedTypes.length > 0 || priceRange.min || priceRange.max) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap gap-2 mb-4 sm:mb-6"
          >
            {selectedTypes.map(type => (
              <span
                key={type}
                className="px-2 sm:px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm flex items-center space-x-1"
              >
                <span>{type}</span>
                <button
                  onClick={() => handleTypeToggle(type)}
                  className="ml-1 hover:text-primary-900"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            ))}
            {(priceRange.min || priceRange.max) && (
              <span className="px-2 sm:px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm flex items-center space-x-1">
                <span>₦{priceRange.min || '0'} - ₦{priceRange.max || '∞'}</span>
                <button
                  onClick={() => setPriceRange({ min: '', max: '' })}
                  className="ml-1 hover:text-primary-900"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            )}
          </motion.div>
        )}

        {/* Results Count */}
        <motion.div
          variants={fadeInUp}
          initial="initial"
          animate="animate"
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4 sm:mb-6"
        >
          <p className="text-sm sm:text-base text-luxury-600">
            Showing <span className="font-semibold text-luxury-900">{accounts?.length || 0}</span> accounts
          </p>
          <p className="text-xs sm:text-sm text-luxury-500">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </motion.div>

        {/* Accounts Grid/List */}
        {isLoading ? (
          <LoadingSkeleton count={6} />
        ) : (
          <motion.div
            variants={{
              animate: {
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
            initial="initial"
            animate="animate"
            className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'
              : 'space-y-3 sm:space-y-4'
            }
          >
            {accounts?.map((account) => {
              const Icon = platformIcons[account.socialMediaType] || FiBox;
              const color = platformColors[account.socialMediaType] || 'from-primary-500 to-secondary-500';

              return viewMode === 'grid' ? (
                // Grid View
                <motion.div
                  key={account._id}
                  variants={fadeInUp}
                >
                  <Link>
                    <GlassCard className="p-4 sm:p-6 h-full hover:shadow-luxury-xl transition-all duration-500">
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div className="flex flex-col gap-1">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-linear-to-r ${color} p-2 sm:p-3 flex items-center justify-center`}>
                            {account.title?.toLowerCase().includes('hma') ? (
                              <img
                                src="https://i.pinimg.com/736x/0f/3d/23/0f3d234572358e98fb27fd74c20ab3ac.jpg"
                                className="w-full h-full object-cover rounded-xl block"
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Icon className="w-6 h-6" />
                            )}
                          </div>
                          <span className="text-xs text-luxury-600 font-medium mt-1">{account.socialMediaType}</span>
                        </div>
                        <div className="flex gap-1 sm:gap-2 flex-col items-end">
                          <span className="badge-success text-xs sm:text-sm">Verified</span>
                          {account.has2FA && (
                            <span className="badge-info text-xs sm:text-sm bg-blue-100 text-blue-700">2FA Enabled</span>
                          )}
                        </div>
                      </div>

                      <h3 className="text-base sm:text-lg font-semibold text-luxury-900 mb-2 line-clamp-2">
                        {account.title}
                      </h3>
                      {account.description && (
                        <p className="text-xs sm:text-sm text-luxury-600 mb-3 sm:mb-4 line-clamp-2">
                          {account.description}
                        </p>
                      )}

                      <p className="text-xs sm:text-sm text-luxury-600 mb-3 sm:mb-4 line-clamp-2">
                        {account.description || 'Premium account'}
                      </p>

                      <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                        <div className="flex items-center text-xs sm:text-sm text-luxury-600">
                          <FiBox className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 shrink-0" />
                          <span className="truncate">Available: {Math.max(0, (account.quantity || 0) - (account.details?.filter(d => d.used).length || 0))} of {account.quantity || 0}</span>
                        </div>
                      </div>

                      <div className="flex items-center mb-2 sm:mb-3">
                        {[...Array(5)].map((_, i) => (
                          <FiStar key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                        <span className="text-xs text-luxury-500 ml-1 sm:ml-2">(4.9)</span>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between pt-3 sm:pt-4 border-t border-luxury-200 gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-lg sm:text-2xl font-bold gradient-text">
                            ₦{account.price?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <Link to={`/accounts/${encodeURIComponent(account.title)}`} state={{ id: account._id }} className="w-full sm:w-auto">
                            <button className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 bg-white/90 text-primary-700 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold border border-gray-200 hover:bg-white">
                              View Details
                            </button>
                          </Link>
                          {(() => {
                            const insufficient = (user?.balance || 0) < (account.price || 0);
                            const isPurchasing = purchasingId === account._id;
                            return (
                              <button
                                onClick={(e) => {  if (!insufficient) setConfirmingAccount(account); else navigate('/deposit'); }}
                                // disabled={insufficient}
                                className={`w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-white font-semibold transition-colors 'bg-primary-600 hover:bg-primary-700`}
                              >
                                {isPurchasing ? 'Purchasing...' : (insufficient ? 'Add Funds' : 'Buy')}
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </GlassCard>
                  </Link>
                </motion.div>
              ) : (
                // List View
                <motion.div
                  key={account._id}
                  variants={fadeInUp}
                >
                  <Link>
                    <GlassCard className="p-3 sm:p-4 hover:shadow-luxury-lg transition-all duration-500">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-12 h-12 rounded-xl bg-linear-to-r ${color} p-2 sm:p-3 flex items-center justify-center flex-shrink-0`}>
                            {account.title?.toLowerCase().includes('hma') ? (
                              <img
                                src="https://i.pinimg.com/736x/0f/3d/23/0f3d234572358e98fb27fd74c20ab3ac.jpg"
                                className="w-full h-full object-cover block"
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Icon className="w-6 h-6" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm sm:text-lg font-semibold text-luxury-900 truncate">
                              {account.title}
                            </h3>
                            {account.description && (
                              <p className="text-xs sm:text-sm text-luxury-600 mt-1">
                                {account.description}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1">
                              <span className="text-xs sm:text-sm text-luxury-500 font-medium">
                                {account.socialMediaType}
                              </span>
                              <span className="text-xs sm:text-sm text-luxury-600">
                                {Math.max(0, (account.quantity || 0) - (account.details?.filter(d => d.used).length || 0))} available
                              </span>
                              <span className="badge-success text-xs">Verified</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-end space-x-3">
                          <span className="text-base sm:text-2xl font-bold gradient-text">
                            ₦{account.price?.toLocaleString()}
                          </span>
                          {/* <Link to={`/accounts/${encodeURIComponent(account.title)}`} state={{ id: account._id }}>
                            <button className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/90 text-primary-700 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold border border-gray-200 hover:bg-white">
                              View
                            </button>
                          </Link> */}
                          {(() => {
                            const insufficient = (user?.balance || 0) < (account.price || 0);
                            const isPurchasing = purchasingId === account._id;
                            return (
                              <button
                                onClick={(e) => {  if (!insufficient) setConfirmingAccount(account); else navigate('/deposit'); }}
                                // disabled={insufficient}
                                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-white font-semibold transition-colors bg-primary-600 hover:bg-primary-700`}
                              >
                                {isPurchasing ? 'Purchasing...' : (insufficient ? 'Add Funds' : 'Buy')}
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </GlassCard>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Load More */}
        {accounts?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-8 sm:mt-12"
          >
            <button className="luxury-button-outline px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base">
              Load More Accounts
            </button>
          </motion.div>
        )}

        {/* No Results */}
        {!isLoading && accounts?.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12 sm:py-20"
          >
            <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-luxury-200 rounded-full flex items-center justify-center">
              <FiSearch className="w-6 h-6 sm:w-8 sm:h-8 text-luxury-400" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-luxury-900 mb-2">
              No Accounts Found
            </h3>
            <p className="text-sm sm:text-base text-luxury-600 mb-4 sm:mb-6">
              Try adjusting your filters or search criteria
            </p>
            <button
              onClick={clearAllFilters}
              className="luxury-button text-sm sm:text-base"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </div>

      {showVideoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl rounded-3xl border border-gray-800 bg-slate-950 p-6 text-white shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold">Video coming soon</h2>
                <p className="mt-2 text-sm text-gray-400">We're preparing a short guide to help you buy virtual numbers.</p>
              </div>
              <button
                type="button"
                onClick={closeVideoModal}
                className="text-2xl font-semibold text-gray-400 hover:text-white"
                aria-label="Close video modal"
              >
                ×
              </button>
            </div>
            <div className="mt-6 rounded-3xl border border-gray-800 bg-slate-900 p-6 text-center">
              <p className="text-lg font-semibold text-white">Video coming soon</p>
              <p className="mt-2 text-sm text-gray-400">A how-to video will be available here shortly.</p>
            </div>
            <div className="mt-6 text-right">
              <button
                type="button"
                onClick={closeVideoModal}
                className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <ConfirmDialog
        isOpen={!!confirmingAccount}
        title={confirmingAccount ? `Confirm purchase: ${confirmingAccount.title}` : ''}
        message={confirmingAccount ? `You are about to purchase "${confirmingAccount.title}" for ₦${(confirmingAccount.price || 0).toLocaleString()}. This will deduct the amount from your wallet and reveal account details to you. Do you wish to continue?` : ''}
        onConfirm={async () => {
          if (!confirmingAccount) return;
          try {
            setPurchasingId(confirmingAccount._id);
            setPurchaseError('');
            await dispatch(purchaseAccount(confirmingAccount._id)).unwrap();
            setConfirmingAccount(null);
            setPurchasingId(null);
            navigate('/orders');
          } catch (err) {
            setPurchaseError(err?.message || 'Purchase failed');
            setConfirmingAccount(null);
            setPurchasingId(null);
          }
        }}
        onCancel={() => setConfirmingAccount(null)}
        confirmText="Buy"
        cancelText="Cancel"
        isDangerous={false}
        isLoading={!!purchasingId}
      />

      {purchaseError && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 max-w-lg w-full px-4">
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 text-rose-800">
            <p className="font-semibold">{purchaseError}</p>
          </div>
        </div>
      )}
    </>
  );
};
